<template>
  <div class="challenge-user-logs-report">
    <div v-if="loading" class="challenge-report-fullscreen-loader">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" @click.prevent="close">
            <i class="ti-arrow-left"></i> {{ $t('general.back') }}
          </a>
        </p>
        <h4 style="margin:0;">{{ headerTitle }}</h4>
      </div>
    </div>

    <div class="card mar-to-15">
      <div class="card-content">
        <p v-if="progressSummary" class="text-muted challenge-progress-summary">
          {{ progressSummary.progress }} / {{ progressSummary.target }} — {{ formatChallengeStatus(progressSummary.status) }}
          <span v-if="progressSummary.completed_at">
            ({{ formatDateTime(progressSummary.completed_at) }})
          </span>
        </p>

        <el-table
          ref="logsTable"
          class="challenge-logs-table"
          :data="tableData"
          style="width: 100%"
          :empty-text="$t('gestiune.noData')"
          show-summary
          :summary-method="getSummaries"
        >
          <el-table-column prop="created_at" :label="$t('challengeCampaigns.dateTime')" min-width="150">
            <template slot-scope="p">{{ formatDateTime(p.row.created_at) || '—' }}</template>
          </el-table-column>
          <el-table-column prop="order_id" :label="$t('challengeCampaigns.orderId')" min-width="120">
            <template slot-scope="p">
              <a href="#" @click.prevent="openOrder(p.row.order_id)">{{ p.row.order_id }}</a>
            </template>
          </el-table-column>
          <el-table-column prop="order_number" :label="$t('orders.orderNumber')" min-width="130" />
          <el-table-column prop="progress" :label="$t('challengeCampaigns.progressGenerated')" min-width="90" />
          <el-table-column prop="products" :label="$t('challengeCampaigns.products')" min-width="160">
            <template slot-scope="p">
              <template v-if="p.row.products && p.row.products.length">
                <span v-for="(prod, idx) in p.row.products" :key="idx">
                  {{ prod.name || ('#' + prod.product_id) }} x{{ prod.quantity }}<span v-if="idx < p.row.products.length - 1">, </span>
                </span>
              </template>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column prop="totals_grandtotal" :label="$t('orders.totals_grandtotal')" min-width="110">
            <template slot-scope="p">
              {{ formatMoneyAmount(p.row.totals_grandtotal, p.row.currency) }}
            </template>
          </el-table-column>
          <el-table-column prop="location_name" :label="$t('orders.location')" min-width="140">
            <template slot-scope="p">{{ formatLocation(p.row) }}</template>
          </el-table-column>
          <el-table-column prop="order_status" :label="$t('orders.status')" min-width="110">
            <template slot-scope="p">{{ formatOrderStatus(p.row.order_status) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    route: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: true,
      tableData: [],
      progressSummary: null,
      ordersGrandTotal: 0,
      challengeTitle: ''
    }
  },
  computed: {
    challengeId () {
      const fromProp = this.route && this.route.params && this.route.params.challengeId
      const fromRouter = this.$route && this.$route.params && this.$route.params.challengeId
      return fromProp || fromRouter
    },
    userId () {
      const fromProp = this.route && this.route.params && this.route.params.userId
      const fromRouter = this.$route && this.$route.params && this.$route.params.userId
      return fromProp || fromRouter
    },
    headerTitle () {
      return this.challengeTitle || (this.$t('challengeCampaigns.userProgress') + ' #' + this.userId)
    },
    locations () {
      const company = this.$session.get('selectedCompanyData') || {}
      return company.locations_details || company.locations || []
    },
    summaryCurrency () {
      const withCurrency = this.tableData.find(row => row.currency)
      return withCurrency ? withCurrency.currency : ''
    }
  },
  created () {
    this.challengeTitle = (this.route && this.route.query && this.route.query.title) || (this.$route.query && this.$route.query.title) || ''
    this.loadLogs()
  },
  methods: {
    formatChallengeStatus (status) {
      if (!status) return '—'
      const key = 'challengeCampaigns.status_' + status
      const translated = this.$t(key)
      return translated === key ? status : translated
    },
    formatOrderStatus (status) {
      if (!status) return '—'
      const key = 'ordersStatus.' + status
      const translated = this.$t(key)
      return translated === key ? status : translated
    },
    formatLocation (row) {
      if (row.location_name) return row.location_name
      if (!row.location_id) return '—'
      const location = this.locations.find(loc =>
        loc.true_id === row.location_id ||
        loc.id === row.location_id ||
        String(loc.true_id) === String(row.location_id) ||
        String(loc.id) === String(row.location_id)
      )
      return location && location.name ? location.name : row.location_id
    },
    getSummaries ({ columns }) {
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t('orders.totals_grandtotal')
          return
        }
        if (column.property === 'totals_grandtotal') {
          const total = this.ordersGrandTotal != null
            ? this.ordersGrandTotal
            : this.tableData.reduce((acc, row) => acc + (Number(row.totals_grandtotal) || 0), 0)
          sums[index] = this.formatMoneyAmount(total, this.summaryCurrency)
          return
        }
        if (column.property === 'progress') {
          sums[index] = this.tableData.reduce((acc, row) => acc + (Number(row.progress) || 0), 0)
          return
        }
        sums[index] = ''
      })
      return sums
    },
    relayoutTable () {
      this.$nextTick(() => {
        if (this.$refs.logsTable && this.$refs.logsTable.doLayout) {
          this.$refs.logsTable.doLayout()
        }
      })
    },
    loadLogs () {
      if (!this.challengeId || !this.userId) return
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest(
        '/reports/challenges/' + this.challengeId + '/users/' + this.userId + '/logs',
        'GET',
        data,
        (response) => {
          this.loading = false
          if (response.status) {
            this.tableData = response.data || []
            const specials = response.data_specials || {}
            this.progressSummary = specials.progress || response.progress || null
            this.ordersGrandTotal = specials.orders_grandtotal != null
              ? Number(specials.orders_grandtotal)
              : this.tableData.reduce((acc, row) => acc + (Number(row.totals_grandtotal) || 0), 0)
            const challenge = specials.challenge || response.challenge
            if (challenge && challenge.title) {
              this.challengeTitle = challenge.title
            }
            this.relayoutTable()
          }
        }
      )
    },
    openOrder (orderId) {
      if (!orderId) return
      this.$router.push('/orders/view/' + orderId)
    },
    close () {
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.challenge-progress-summary {
  margin-bottom: 12px;
  font-size: 14px;
}
/* Keep header / body / footer column widths in sync when show-summary is on */
.challenge-logs-table >>> .el-table__header-wrapper,
.challenge-logs-table >>> .el-table__body-wrapper,
.challenge-logs-table >>> .el-table__footer-wrapper {
  width: 100% !important;
}
.challenge-logs-table >>> .el-table__header,
.challenge-logs-table >>> .el-table__body,
.challenge-logs-table >>> .el-table__footer {
  width: 100% !important;
  table-layout: fixed;
}
.challenge-logs-table >>> col[name='gutter'],
.challenge-logs-table >>> th.gutter,
.challenge-logs-table >>> td.gutter {
  display: none !important;
  width: 0 !important;
}
.challenge-report-fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}
.challenge-report-fullscreen-loader .el-loading-spinner {
  position: static;
  margin-top: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/ChallengeUserProgressLogsReport.vue